import { createServerFn } from '@tanstack/react-start';
import { getRequest } from '@tanstack/react-start/server';
import { createIntl } from 'react-intl';

import { getUpcomingCalendarByCompanyIds } from '~/database/supabase/calendar/queries.server.ts';
import {
	getCompaniesAndInstrumentsBasicInfo,
	getUserCompanyIds,
} from '~/database/supabase/instrument_company/queries.server.ts';
import type { CompanyNamesAndInstrumentBasicInfoType } from '~/database/supabase/instrument_company/types.ts';
import { getLatestNewsByCompanyIds } from '~/database/supabase/news/queries.server.ts';
import { getUserPortfolios } from '~/database/supabase/portfolio/queries.server.ts';
import type { PortfolioWithAllRelationsType } from '~/database/supabase/portfolio/types.ts';
import { getAllPortfolioValues } from '~/database/supabase/prices/queries.server.ts';
import type { CleanPortfolioValue } from '~/database/supabase/prices/types.ts';
import { getMessages, getUiLanguage } from '~/i18n/messages.ts';
import { fetchDisplayData } from '~/integrations/yahoo/queries.server.ts';
import { getMostPopularTicker } from '~/modules/notification/utils.ts';
import { getHints } from '~/utils/client-hints.tsx';

import { requireStartAuthSession } from '../auth/require-start-auth-session.ts';
import { createServerFnObservabilityMiddleware } from '../utils/create-instrumented-server-fn.ts';

export interface DashboardPortfolioSummary {
	readonly currency: string;
	readonly currentValue: number | null;
	readonly holdingsCount: number;
	readonly id: number;
	readonly name: string;
	readonly weeklyAbsolute: number | null;
	readonly weeklyPercent: number | null;
}

export interface DashboardHeadline {
	readonly companyId: number;
	readonly companyName: string;
	readonly dayChange: string | null;
	readonly dayVolume: string | null;
	readonly description: string;
	readonly href: string | null;
	readonly id: number;
	readonly lang: string;
	readonly publishedAt: number;
	readonly ticker: string;
}

export interface DashboardEvent {
	readonly companyId: number;
	readonly companyName: string;
	readonly date: string | null;
	readonly description: string;
	readonly id: number;
	readonly ticker: string;
}

export interface DashboardMarketItem {
	readonly changePercent: number | null;
	readonly label: string;
	readonly price: number | null;
	readonly shortName: string;
	readonly ticker: string;
}

export interface DashboardPageData {
	readonly calendar: DashboardEvent[];
	readonly countryBenchmarks: DashboardMarketItem[];
	readonly currencyBenchmarks: DashboardMarketItem[];
	readonly headlines: DashboardHeadline[];
	readonly portfolioSummaries: DashboardPortfolioSummary[];
	readonly userLocale: string;
}

const ONE_WEEK_IN_SECONDS = 7 * 24 * 60 * 60;

const COUNTRY_INDEX_BY_NAME: Record<string, { regionCode: string; ticker: string }> = {
	Canada: { regionCode: 'CA', ticker: '^GSPTSE' },
	Denmark: { regionCode: 'DK', ticker: '^OMXC25' },
	Finland: { regionCode: 'FI', ticker: '^OMXH25' },
	France: { regionCode: 'FR', ticker: '^FCHI' },
	Germany: { regionCode: 'DE', ticker: '^GDAXI' },
	Norway: { regionCode: 'NO', ticker: 'OSEBX.OL' },
	Sweden: { regionCode: 'SE', ticker: '^OMX' },
	'United Kingdom': { regionCode: 'GB', ticker: '^FTSE' },
	'United States': { regionCode: 'US', ticker: '^GSPC' },
};

const CURRENCY_PAIR_BY_CODE: Record<string, { label: string; ticker: string }> = {
	CAD: { label: 'CAD/SEK', ticker: 'CADSEK=X' },
	DKK: { label: 'DKK/SEK', ticker: 'DKKSEK=X' },
	EUR: { label: 'EUR/SEK', ticker: 'EURSEK=X' },
	GBP: { label: 'GBP/SEK', ticker: 'GBPSEK=X' },
	NOK: { label: 'NOK/SEK', ticker: 'NOKSEK=X' },
	USD: { label: 'USD/SEK', ticker: 'SEK=X' },
};

function getComparisonValue(values: CleanPortfolioValue[]): number | null {
	const latestTimestamp = values[0]?.timestamp;
	if (!latestTimestamp) {
		return null;
	}

	const comparisonTimestamp = latestTimestamp - ONE_WEEK_IN_SECONDS;
	const comparisonRow = values.find((value) => value.timestamp <= comparisonTimestamp) ?? values.at(-1);

	return comparisonRow?.close ?? null;
}

function buildPortfolioSummary(
	portfolio: PortfolioWithAllRelationsType,
	values: CleanPortfolioValue[] | null,
): DashboardPortfolioSummary {
	const holdingsCount = portfolio.position.filter((position) => position.size !== 0).length;
	const currentValue = values?.[0]?.close ?? null;
	const comparisonValue = values ? getComparisonValue(values) : null;
	const weeklyAbsolute = currentValue !== null && comparisonValue !== null ? currentValue - comparisonValue : null;
	const weeklyPercent =
		currentValue !== null && comparisonValue !== null && comparisonValue !== 0
			? (currentValue / comparisonValue - 1) * 100
			: null;

	return {
		currency: portfolio.currency,
		currentValue,
		holdingsCount,
		id: portfolio.id,
		name: portfolio.name,
		weeklyAbsolute,
		weeklyPercent,
	};
}

function buildCompanyMetaMap(
	companies: CompanyNamesAndInstrumentBasicInfoType[] | null,
): Map<number, { companyName: string; ticker: string }> {
	const companyMeta = new Map<number, { companyName: string; ticker: string }>();

	for (const company of companies ?? []) {
		const ticker = getMostPopularTicker(company.instrument);
		companyMeta.set(Number(company.id), {
			companyName: company.name ?? ticker ?? 'Unknown company',
			ticker: ticker || '-',
		});
	}

	return companyMeta;
}

function countExposure(items: string[]): Array<[string, number]> {
	const counts = new Map<string, number>();

	for (const item of items) {
		if (!item) {
			continue;
		}

		counts.set(item, (counts.get(item) ?? 0) + 1);
	}

	return Array.from(counts.entries()).sort((left, right) => right[1] - left[1]);
}

function getCountryBenchmarkTickers(
	portfolios: PortfolioWithAllRelationsType[] | null | undefined,
	userLocale: string,
): DashboardMarketItem[] {
	const locale = getUiLanguage(userLocale);
	const intl = createIntl({ locale, messages: getMessages(locale) });
	const regionNames = new Intl.DisplayNames([locale], { type: 'region' });
	const activeCountries = (portfolios ?? [])
		.flatMap((portfolio) => portfolio.position)
		.filter((position) => position.size !== 0)
		.map((position) => position.instrument.company?.country ?? '')
		.filter(Boolean);

	return countExposure(activeCountries)
		.map(([country]) => COUNTRY_INDEX_BY_NAME[country])
		.filter((value): value is { regionCode: string; ticker: string } => Boolean(value))
		.slice(0, 3)
		.map((value) => {
			const label = intl.formatMessage(
				{ id: 'dashboard.countryMarket' },
				{ country: regionNames.of(value.regionCode) ?? value.regionCode },
			);
			return { changePercent: null, label, price: null, shortName: label, ticker: value.ticker };
		});
}

function getCurrencyBenchmarkTickers(
	portfolios: PortfolioWithAllRelationsType[] | null | undefined,
): DashboardMarketItem[] {
	const activeCurrencies = (portfolios ?? [])
		.flatMap((portfolio) => portfolio.position)
		.filter((position) => position.size !== 0)
		.map((position) => position.instrument.currency)
		.filter(Boolean);

	return countExposure(activeCurrencies)
		.map(([currency]) => CURRENCY_PAIR_BY_CODE[currency])
		.filter((value): value is { label: string; ticker: string } => Boolean(value))
		.slice(0, 3)
		.map((value) => ({
			changePercent: null,
			label: value.label,
			price: null,
			shortName: value.label,
			ticker: value.ticker,
		}));
}

async function hydrateMarketItems(items: DashboardMarketItem[]): Promise<DashboardMarketItem[]> {
	const uniqueItems = items.filter(
		(item, index, source) => source.findIndex((entry) => entry.ticker === item.ticker) === index,
	);

	return Promise.all(
		uniqueItems.map(async (item) => {
			const data = await fetchDisplayData({ ticker: item.ticker });

			return {
				changePercent: data?.regularMarketChangePercent ?? null,
				label: item.label,
				price: data?.regularMarketPrice ?? null,
				shortName: data?.shortName ?? data?.displayName ?? item.shortName,
				ticker: item.ticker,
			};
		}),
	);
}

export const getDashboardPageData = createServerFn({ method: 'GET' })
	.middleware([
		createServerFnObservabilityMiddleware({
			dependencies: [
				'auth.session',
				'supabase.portfolio.full',
				'supabase.portfolio-values',
				'supabase.user-company-ids',
				'supabase.company-basics',
				'supabase.news.latest',
				'supabase.calendar.upcoming',
				'yahoo.display-data',
			],
			relatesTo: 'dashboard.page',
		}),
	])
	.handler(async () => {
		const request = getRequest();
		const authSession = await requireStartAuthSession({ request });
		const userLocale = getHints(request).locale;

		const [portfolios, companyIds] = await Promise.all([
			getUserPortfolios(authSession.userId),
			getUserCompanyIds({ userId: authSession.userId }),
		]);

		const portfolioSummaries = await Promise.all(
			(portfolios ?? []).map(async (portfolio) => {
				const values = await getAllPortfolioValues(portfolio.id, { daysBack: 8, limit: 120 });
				return buildPortfolioSummary(portfolio, values);
			}),
		);

		const [countryBenchmarks, currencyBenchmarks] = await Promise.all([
			hydrateMarketItems(getCountryBenchmarkTickers(portfolios, userLocale)),
			hydrateMarketItems(getCurrencyBenchmarkTickers(portfolios)),
		]);

		if (!companyIds || companyIds.length === 0) {
			return {
				calendar: [],
				countryBenchmarks,
				currencyBenchmarks,
				headlines: [],
				portfolioSummaries,
				userLocale,
			} satisfies DashboardPageData;
		}

		const [companies, newsRows, calendarRows] = await Promise.all([
			getCompaniesAndInstrumentsBasicInfo(companyIds),
			getLatestNewsByCompanyIds(companyIds, 40),
			getUpcomingCalendarByCompanyIds(companyIds, 20),
		]);

		const companyMeta = buildCompanyMetaMap(companies);
		const weekAgo = Date.now() - ONE_WEEK_IN_SECONDS * 1000;
		const nextWeek = Date.now() + ONE_WEEK_IN_SECONDS * 1000;

		const headlines = (newsRows ?? [])
			.map<DashboardHeadline | null>((row) => {
				const publishedAt = Date.parse(`${row.date}T${row.time}`);
				if (Number.isNaN(publishedAt) || publishedAt < weekAgo) {
					return null;
				}

				const company = companyMeta.get(Number(row.company_id));

				return {
					companyId: Number(row.company_id),
					companyName: company?.companyName ?? 'Unknown company',
					dayChange: row.day_change,
					dayVolume: row.day_volume,
					description: row.description,
					href: row.href ?? null,
					id: row.id,
					lang: row.lang.toLowerCase(),
					publishedAt,
					ticker: company?.ticker ?? '-',
				};
			})
			.filter((headline): headline is DashboardHeadline => headline !== null)
			.slice(0, 8);

		const calendar = (calendarRows ?? [])
			.filter((row) => {
				const epoch = row.date ? Date.parse(row.date) : Number.NaN;
				return !Number.isNaN(epoch) && epoch <= nextWeek;
			})
			.map<DashboardEvent>((row) => {
				const company = companyMeta.get(Number(row.company_id));

				return {
					companyId: Number(row.company_id),
					companyName: company?.companyName ?? 'Unknown company',
					date: row.date,
					description: row.description,
					id: row.id,
					ticker: company?.ticker ?? '-',
				};
			})
			.slice(0, 8);

		return {
			calendar,
			countryBenchmarks,
			currencyBenchmarks,
			headlines,
			portfolioSummaries,
			userLocale,
		} satisfies DashboardPageData;
	});
